import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { DOCUMENT_TYPES, PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, ErrorResponse, Spinner, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import { columnsFactory } from '~support-at-home/forms/dashboard/claimItemsDashboardList';
import { searchFields } from '~support-at-home/forms/dashboard/claimItemsDashboardSearch';

import { useGetSahClaimStatusesQuery } from '~lookups/actions/sahClaimStatuses';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { getAll as getClients } from '~main/actions/clients';
import { CreateDocumentBatchJob, useCreateDocumentBatchMutation } from '~main/actions/documentBatches';
import { useGetDashboardClaimItemsQuery } from '~support-at-home/actions/dashboardClaimItems';

export const ClaimItemsDashboard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [createDocumentBatch, { isLoading: isGenerating }] = useCreateDocumentBatchMutation();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearch('claim-items-dashboard-search', {});

  const search = useCallback(
    (values: Record<string, any>) => {
      setSearchParams(values);
      setCurrentPage(1);
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (isActive) {
      dispatch(getClients({ disabled: undefined, attributes: ['id', 'full_name', 'non_chargeable', 'disabled'] }));
    }
  }, [isActive]);

  const queryArgs = useMemo(
    () => ({
      ...searchParams,
      limit: PAGE_SIZE_SM,
      offset: (currentPage - 1) * PAGE_SIZE_SM,
    }),
    [currentPage, searchParams],
  );

  const { data: claimStatuses = [] } = useGetSahClaimStatusesQuery(undefined, { skip: !isActive });
  const { data: sahServices = [] } = useGetSahServicesQuery(undefined, { skip: !isActive });
  const { data, isLoading, isFetching, isError, error } = useGetDashboardClaimItemsQuery(queryArgs, {
    skip: !isActive,
  });

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNavigateToClaim = useCallback(
    (claimId: string) => {
      navigate(`/support-at-home/claims/${claimId}`);
    },
    [navigate],
  );

  const onGenerateCsv = async () => {
    try {
      const job = {
        document_type: DOCUMENT_TYPES.SAH_CLAIM_ITEMS.CSV,
        ...searchParams,
      } as CreateDocumentBatchJob & Record<string, unknown>;
      await createDocumentBatch([job]).unwrap();
      showToast('success', 'CSV generation started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start CSV generation';
      showToast('danger', message);
    }
  };

  const columns = useMemo(() => columnsFactory(handleNavigateToClaim), [handleNavigateToClaim]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading" role="status" aria-live="polite">
          <Spinner loading={true} type="beat" size={10} />
          Loading claim items...
        </div>
      );
    }

    if (isError) {
      return (
        <ErrorResponse
          message={`Failed to load claim items - ${getErrorMessage(error)}`}
          dataCy="claim-items-dashboard-error"
        />
      );
    }

    return (
      <TableView
        id="claim-items-table"
        columns={columns}
        data={data?.rows ?? []}
        isLoading={isFetching}
        emptyMessage="No claim items found for selected filters"
        allowPagination
        paginationType="pages"
        showCount
        currentPage={currentPage}
        pageSize={PAGE_SIZE_SM}
        totalCount={data?.total ?? 0}
        onPageChange={handlePageChange}
        readonly
        bordered
      />
    );
  };

  return (
    <div
      className={`claim-items-dashboard${isFetching ? ' fetching' : ''}`}
      data-cy="claim-items-dashboard"
      aria-busy={isFetching}
    >
      <Toolbar
        id="claim-items-dashboard"
        rightCluster={
          <>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="claim-items-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              aria-expanded={searchBarOpen}
              aria-controls="claim-items-searchbar"
            />
            <Button size="sm" color="secondary" loading={isGenerating} disabled={isGenerating} onClick={onGenerateCsv}>
              Generate CSV
            </Button>
          </>
        }
      />
      <SearchBar
        id="claim-items-searchbar"
        isOpen={searchBarOpen}
        fields={searchFields(claimStatuses, sahServices)}
        initialValues={searchParams}
        onSubmit={search}
        storageKey="claim-items-dashboard"
      />
      {renderContent()}
    </div>
  );
};
